import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  LuCloudUpload,
  LuEye,
  LuFile,
  LuFileText,
  LuImage,
  LuLoaderCircle,
  LuSave,
  LuTrash2,
  LuWallet,
} from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import { Field, TextArea, TextInput } from '../../components/form/FormControls';
import { useTranslation } from '../../i18n/LanguageContext';
import { EXPENSE_TYPE_STYLES } from './visitModal/expenseTypes';
// Only global theme variables are left in this file; the import keeps them loaded.
import './ExpenseModal.module.css';

const FORM_ID = 'vm-expense-form';

/**
 * "Masraf Ekle" for a visit. Files go straight to Storage; the expense is
 * appended to Visits/{visitId}.expenses on save and verified. Logic unchanged;
 * the dialog is the portal's Tailwind modal.
 */
const ExpenseModal = ({ show, onHide, visitId, onExpenseAdded }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`visitModal.expenses.dialog.${key}`, vars);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    cost: '',
    information: '',
    attachments: []
  });
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const expenseTypes = ['transportation', 'accommodation', 'meal', 'supplies', 'other'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Masraf adı gerekli';
    }

    if (!formData.type) {
      newErrors.type = 'Masraf tipi seçilmeli';
    }

    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      newErrors.cost = 'Geçerli bir maliyet giriniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return LuImage;
    if (fileType === 'application/pdf') return LuFileText;
    return LuFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFileToStorage = async (file) => {
    const timestamp = Date.now();
    const fileName = `expenses/${visitId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        name: file.name,
        url: downloadURL,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        storagePath: fileName
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleFileUpload = async (files) => {
    const fileList = Array.from(files);

    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    const validFiles = fileList.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Desteklenmeyen dosya türü. Sadece resim ve PDF dosyaları yükleyebilirsiniz.`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: Dosya boyutu çok büyük. Maksimum 5MB yükleyebilirsiniz.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = validFiles.map(file => uploadFileToStorage(file));
      const uploadedFiles = await Promise.all(uploadPromises);

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));

      toast.success(`${uploadedFiles.length} dosya başarıyla yüklendi`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = formData.attachments[index];

    try {
      // Delete from Firebase Storage
      if (attachment.storagePath) {
        const storageRef = ref(storage, attachment.storagePath);
        await deleteObject(storageRef);
      }

      // Remove from state
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index)
      }));

      toast.success('Dosya silindi');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Dosya silinirken bir hata oluştu');
    }
  };

  const openFilePreview = (url) => {
    window.open(url, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Lütfen formu kontrol ediniz');
      return;
    }

    if (!visitId) {
      toast.error('Ziyaret ID bulunamadı');
      return;
    }

    setSaving(true);
    try {
      const visitRef = doc(db, 'Visits', visitId);
      const expenseData = {
        ...formData,
        cost: parseFloat(formData.cost),
        createdAt: new Date().toISOString(),
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Add unique ID for expense
      };

      console.log('Saving expense to Firebase:', expenseData);
      console.log('Visit ID:', visitId);

      await updateDoc(visitRef, {
        expenses: arrayUnion(expenseData),
        lastUpdated: new Date().toISOString()
      });

      // Verify the expense was saved by fetching the document
      const updatedDoc = await getDoc(visitRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        const savedExpense = updatedData.expenses?.find(exp => exp.id === expenseData.id);
        if (savedExpense) {
          console.log('Expense verified in Firebase:', savedExpense);
          toast.success('Masraf başarıyla eklendi ve kaydedildi');
          onExpenseAdded(expenseData);
          resetForm();
          onHide();
        } else {
          throw new Error('Masraf kaydedildi ancak doğrulanamadı');
        }
      } else {
        throw new Error('Ziyaret dokümanı bulunamadı');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      console.error('Visit ID was:', visitId);
      console.error('Expense data was:', formData);
      toast.error('Masraf eklenirken bir hata oluştu: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      cost: '',
      information: '',
      attachments: []
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleTypeSelect = (typeValue) => {
    handleInputChange('type', typeValue);
  };

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={() => !saving && handleClose()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuWallet aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>{label('cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving || uploading} className="max-sm:tw-flex-1">
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('saving') : label('save')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-5">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-[minmax(0,1fr)_11rem]">
          <Field label={label('name')} htmlFor="vm-expense-name" required error={errors.name || undefined}>
            <TextInput
              id="vm-expense-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={label('namePlaceholder')}
              error={errors.name || undefined}
            />
          </Field>
          <Field label={label('cost')} htmlFor="vm-expense-cost" required error={errors.cost || undefined}>
            <span className="tw-relative tw-block">
              <TextInput
                id="vm-expense-cost"
                type="number"
                step="0.01"
                min="0"
                value={formData.cost}
                onChange={(e) => handleInputChange('cost', e.target.value)}
                placeholder="0.00"
                error={errors.cost || undefined}
                className="tw-pr-9"
              />
              <span className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-font-semibold tw-text-slate-400">₺</span>
            </span>
          </Field>
        </div>

        <Field label={label('type')} required error={errors.type || undefined}>
          <div role="radiogroup" aria-label={label('type')} className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-5">
            {expenseTypes.map((type) => {
              const { icon: Icon, tone } = EXPENSE_TYPE_STYLES[type];
              const selected = formData.type === type;
              return (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => handleTypeSelect(type)}
                  className={`tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-2xl tw-border tw-px-2 tw-py-3 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    selected
                      ? 'tw-border-teal-500/50 tw-bg-teal-500/10 tw-text-teal-800 dark:tw-text-teal-200'
                      : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07]'
                  }`}
                >
                  <span className={`tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl tw-ring-1 tw-ring-inset ${tone}`}>
                    <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
                  </span>
                  {t(`visitModal.expenses.types.${type}`)}
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label={<>{label('info')} <span className="tw-font-normal tw-text-slate-400">{label('optional')}</span></>}
          htmlFor="vm-expense-info"
        >
          <TextArea
            id="vm-expense-info"
            rows={3}
            value={formData.information}
            onChange={(e) => handleInputChange('information', e.target.value)}
            placeholder={label('infoPlaceholder')}
          />
        </Field>

        <Field label={<>{label('files')} <span className="tw-font-normal tw-text-slate-400">{label('filesHint')}</span></>}>
          <div
            role="button"
            tabIndex={0}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-6 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
              dragActive
                ? 'tw-border-teal-500 tw-bg-teal-500/[0.07]'
                : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02]'
            }`}
          >
            {uploading
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-8 tw-w-8 tw-animate-spin tw-text-teal-500" />
              : <LuCloudUpload aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-teal-500" />}
            <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
              {uploading ? label('uploading') : (
                <>
                  {label('drop')} <span className="tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">{label('dropLink')}</span>
                </>
              )}
            </p>
            <p className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{label('dropHint')}</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="tw-hidden"
            />
          </div>

          {formData.attachments.length > 0 && (
            <ul className="tw-mt-2 tw-space-y-2">
              {formData.attachments.map((attachment, index) => {
                const FileIcon = getFileIcon(attachment.type);
                return (
                  <li key={index} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/80 tw-py-1.5 tw-pl-3 tw-pr-1.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <FileIcon aria-hidden="true" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-slate-400" />
                    <div className="tw-min-w-0 tw-flex-1">
                      <p className="tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{attachment.name}</p>
                      <p className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{formatFileSize(attachment.size)}</p>
                    </div>
                    <IconButton label={label('preview')} onClick={() => openFilePreview(attachment.url)}>
                      <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </IconButton>
                    <IconButton label={label('delete')} tone="alert" onClick={() => removeAttachment(index)}>
                      <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </IconButton>
                  </li>
                );
              })}
            </ul>
          )}
        </Field>
      </form>
    </Modal>
  );
};

ExpenseModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  visitId: PropTypes.string,
  onExpenseAdded: PropTypes.func.isRequired,
};

export default ExpenseModal;
